import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'

import CategoriesSection from '../../../../reusableFormFields/CategoriesSection'
import EditForm from '../../../../../../features/components/Forms/EditForm'
import KeywordsSection from '../../../../reusableFormFields/KeywordsSection'
import RadioBlock from '../../../../../../components/Form/RadioBlock'
import KeywordExclusionListsSection from '../../../../reusableFormFields/KeywordExclusionListsSection'

import { useGetAttachedNegativeKeywordsLists } from './hooks'

import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import {
  dv360BulkTargetingWasUpdatedSelector,
  dv360NegativeKeywordListsWasLoadedSelector,
  dv360TargetingSelector,
  updateDv360BulkTargetingErrorSelector,
  updateDv360BulkTargetingIsLoadingSelector
} from '../../../../../../modules/selectors/dv360Constants'
import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import {
  clearUpdateDv360BulkTargeting,
  updateDv360BulkTargeting
} from '../../../../../../modules/actions/dv360Constants'

import { HISTORY_META_EDIT_SECTION, TYPE_LINE_ITEM } from '../../../../../../constants/changeHistory'
import {
  CATEGORIES,
  EXCLUDED_CATEGORIES,
  KEYWORD_EXCLUSION_LISTS,
  KEYWORDS_TABLE,
  TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS,
  YES_USING_PRE_SAVED_KEYWORD_EXCLUSION_LISTS
} from '../../../../UploadDV360AdCreative/UploadDV360AdCreativeForm/LineItemForm/fields'
import { targetingTypes } from '../../../LineItemDv360Create/fields'
import { OPTION_YES } from '../../../../../../constants/forms'
import { getInitialValues } from './fields'

function CategoriesAndKeywordsSectionForm({ editItemData: item, title, ...formProps }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const targeting = useSelector(dv360TargetingSelector)
  const dv360BulkTargetingWasUpdated = useSelector(dv360BulkTargetingWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)
  const dv360NegativeKeywordListsWasLoaded = useSelector(dv360NegativeKeywordListsWasLoadedSelector)

  const attachedNegativeKeywordsLists = useGetAttachedNegativeKeywordsLists()

  const initialValues = useMemo(() => {
    return getInitialValues(targeting, attachedNegativeKeywordsLists)
  }, [targeting, attachedNegativeKeywordsLists])

  const handleSubmit = useCallback(
    values => {
      const categoriesTargeting = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_CATEGORY,
          assigned_targeting_options: [
            ...values[CATEGORIES].map(category => ({
              targeting_option_id: category.value,
              negative: false
            })),
            ...values[EXCLUDED_CATEGORIES].map(category => ({
              targeting_option_id: category.value,
              negative: true
            }))
          ]
        }
      ]

      const filledKeywords = values[KEYWORDS_TABLE].filter(keywordRow => keywordRow[0].value).map(
        keywordRow => keywordRow[0]
      )
      const filledExcludedKeywords = values[KEYWORDS_TABLE].filter(keywordRow => keywordRow[1].value).map(
        keywordRow => keywordRow[1]
      )

      const keywordExclusionLists = values[KEYWORD_EXCLUSION_LISTS]

      const keywordsTargeting = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_KEYWORD,
          assigned_targeting_options:
            values[TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS] === OPTION_YES
              ? [
                  ...filledKeywords.map(keyword => ({
                    keyword: keyword.value
                  })),
                  ...filledExcludedKeywords.map(keyword => ({
                    keyword: keyword.value,
                    negative: true
                  }))
                ]
              : // remove options if another radio button is selected
                []
        }
      ]

      const keywordExclusionTargeting = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_NEGATIVE_KEYWORD_LIST,
          assigned_targeting_options:
            values[TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS] === YES_USING_PRE_SAVED_KEYWORD_EXCLUSION_LISTS
              ? keywordExclusionLists.map(item => ({
                  negative_keyword_list_id: item.value
                }))
              : // remove options if another radio button is selected
                []
        }
      ]

      const updateData = {
        account: selectedAdAccountId,
        line_item_id: item.id,
        requests: [...categoriesTargeting, ...keywordsTargeting, ...keywordExclusionTargeting],
        meta: {
          [HISTORY_META_EDIT_SECTION]: title,
          override: {
            item_id: item.id,
            item_type: TYPE_LINE_ITEM,
            action: 'update'
          }
        }
      }

      dispatch(updateDv360BulkTargeting(updateData, true))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values, setFieldValue } = formik

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateDv360BulkTargeting())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || dv360BulkTargetingWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={updateDv360BulkTargetingErrorSelector}
      isLoadingSelector={updateDv360BulkTargetingIsLoadingSelector}
      {...formProps}
    >
      <br />
      <RadioBlock
        setFieldValue={setFieldValue}
        id={TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS + YES_USING_PRE_SAVED_KEYWORD_EXCLUSION_LISTS}
        name={TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS}
        value={YES_USING_PRE_SAVED_KEYWORD_EXCLUSION_LISTS}
        selectedValue={values[TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS]}
        label={t('Pre-saved keyword exclusion Lists')}
      >
        <CategoriesSection formik={formik} />
        {!dv360NegativeKeywordListsWasLoaded ? (
          <Skeleton count={4} />
        ) : (
          <KeywordExclusionListsSection formik={formik} />
        )}
      </RadioBlock>
      <RadioBlock
        setFieldValue={setFieldValue}
        id={TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS + OPTION_YES}
        name={TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS}
        value={OPTION_YES}
        selectedValue={values[TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS]}
        label={t('Manually entering the keywords')}
      >
        <CategoriesSection formik={formik} />
        <KeywordsSection formik={formik} />
      </RadioBlock>
    </EditForm>
  )
}

export default CategoriesAndKeywordsSectionForm
